import { useEffect, useState, type KeyboardEvent } from "react";
import { Loader2, X } from "lucide-react";
import type { ContactSuggestion, EmailParticipant } from "@onemileauto/shared-types";
import { isValidEmail } from "../../lib/compose-text";
import { useContacts } from "../../hooks/use-contacts";

interface RecipientInputProps {
  label: string;
  value: EmailParticipant[];
  onChange: (next: EmailParticipant[]) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /**
   * Scopes recipient-autocomplete suggestions to this mailbox's own
   * correspondence history (see hooks/use-contacts.ts). Omit to disable
   * suggestions entirely — manual entry (type a full address, press Enter/
   * comma/Tab, or just blur) still works exactly as before.
   */
  mailboxId?: string;
}

// Long enough that a fast typist doesn't fire a request per keystroke, short
// enough that suggestions still feel instant — shorter than the 400ms
// reset-password-page.tsx uses for its username-availability check, since
// that's a real uniqueness lookup with its own cost consideration, while
// this is just filtering an already-small, already-fetched contact list.
const CONTACT_QUERY_DEBOUNCE_MS = 200;

// Client feedback: suggestions popping up immediately on focus (from a
// blank query) felt premature — wait for a real signal of intent before
// proposing anything.
const MIN_QUERY_LENGTH = 3;

/**
 * Chip-based multi-recipient field: type an address, press Enter/comma (or
 * blur/tab away) to commit it as a chip, tap the × to remove. Once at least
 * MIN_QUERY_LENGTH characters are typed, a dropdown of matching contacts —
 * derived from this mailbox's real correspondence history, never a
 * manually-managed address book (see apps/mail's db/do/schema.sql `contacts`
 * table docstring) — appears below it; tapping/clicking a suggestion adds it as a chip the
 * same way confirming a manually-typed address does. Generous touch targets
 * throughout (py-3 rows, ~44px+ tall) per this app's non-technical-user
 * requirement — selection works by click/tap as the primary path, with
 * arrow-key navigation + Enter as an addition for anyone who wants it, never
 * the only way in.
 */
export function RecipientInput({ label, value, onChange, placeholder, autoFocus, mailboxId }: RecipientInputProps) {
  const [draft, setDraft] = useState("");
  const [debouncedDraft, setDebouncedDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedDraft(draft), CONTACT_QUERY_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [draft]);

  const meetsQueryThreshold = debouncedDraft.trim().length >= MIN_QUERY_LENGTH;
  const contactsQuery = useContacts(mailboxId, debouncedDraft, { enabled: open && meetsQueryThreshold });
  const alreadyAdded = new Set(value.map((p) => p.email.toLowerCase()));
  const suggestions = (contactsQuery.data?.contacts ?? []).filter((c) => !alreadyAdded.has(c.email.toLowerCase()));

  const showDropdown = Boolean(mailboxId) && open && meetsQueryThreshold;

  function addRecipient(participant: EmailParticipant) {
    if (!value.some((p) => p.email.toLowerCase() === participant.email.toLowerCase())) {
      onChange([...value, participant]);
    }
    setDraft("");
    setDebouncedDraft("");
    setHighlightIndex(-1);
    // Reopen with fresh top suggestions (now excluding the one just added)
    // so adding several recipients in a row is click-click-click, not
    // click-then-refocus-then-click.
    setOpen(true);
  }

  function commit(raw: string) {
    const email = raw.trim().replace(/,$/, "");
    if (!email) return;
    addRecipient({ email, name: null });
  }

  function selectSuggestion(contact: ContactSuggestion) {
    addRecipient({ email: contact.email, name: contact.name });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (showDropdown && suggestions.length > 0 && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      const dir = e.key === "ArrowDown" ? 1 : -1;
      setHighlightIndex((prev) => {
        const next = prev + dir;
        if (next < 0) return suggestions.length - 1;
        if (next >= suggestions.length) return 0;
        return next;
      });
      return;
    }
    if (e.key === "Enter" && showDropdown && highlightIndex >= 0 && suggestions[highlightIndex]) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightIndex]);
      return;
    }
    if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        commit(draft);
      }
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  function remove(email: string) {
    onChange(value.filter((p) => p.email !== email));
  }

  const listboxId = `contact-suggestions-${label.toLowerCase()}`;

  return (
    <div className="relative flex items-start gap-3 border-b border-border py-2.5">
      <span className="mt-2 w-16 shrink-0 text-sm text-muted">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 py-0.5">
        {value.map((p) => (
          <span
            key={p.email}
            className="inline-flex items-center gap-1 rounded-full bg-parchment-dim py-1 pl-3 pr-1.5 text-sm text-charcoal"
          >
            {p.name || p.email}
            <button
              type="button"
              onClick={() => remove(p.email)}
              aria-label={`Remove ${p.email}`}
              className="flex h-5 w-5 items-center justify-center rounded-full text-muted hover:bg-border hover:text-charcoal"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          type="email"
          inputMode="email"
          autoFocus={autoFocus}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setHighlightIndex(-1);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            commit(draft);
            setOpen(false);
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          className="min-w-[8rem] flex-1 bg-transparent py-1 text-[15px] text-charcoal placeholder:text-muted focus:outline-none"
          aria-label={label}
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls={listboxId}
        />
        {draft && !isValidEmail(draft) && (
          <span className="w-full text-xs text-danger">Doesn&rsquo;t look like a full email address yet</span>
        )}
      </div>

      {showDropdown && (
        <div
          id={listboxId}
          role="listbox"
          aria-label={`${label} contact suggestions`}
          className="absolute inset-x-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-white shadow-lg"
        >
          {contactsQuery.isLoading ? (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted">
              <Loader2 size={16} className="animate-spin" />
              Looking up contacts…
            </div>
          ) : suggestions.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted">No matching contacts — you can still add this as a new address.</p>
          ) : (
            suggestions.map((c, i) => (
              <button
                key={c.email}
                type="button"
                role="option"
                aria-selected={i === highlightIndex}
                // preventDefault on mousedown (fired before the input's blur
                // on both mouse and touch) keeps the input focused so this
                // button's onClick still fires normally, instead of the
                // dropdown closing (via the input's onBlur) a beat before
                // the tap/click registers.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectSuggestion(c)}
                onMouseEnter={() => setHighlightIndex(i)}
                className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                  i === highlightIndex ? "bg-parchment-dim" : "hover:bg-parchment-dim"
                }`}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rust/15 text-sm font-medium text-rust-deep">
                  {(c.name || c.email).charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] text-charcoal">{c.name || c.email}</span>
                  {c.name && <span className="block truncate text-xs text-muted">{c.email}</span>}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
